import Control from "../1. Core/Control";
import NP from "../1. Core/NP";
import TopAppBar from "../1. Core/Controls/TopAppBar/TopAppBar";
import Button from "../1. Core/Controls/Button/Button";
import DataTable from "../1. Core/Controls/DataTable/DataTable";
import Select from "../1. Core/Controls/Select/Select";
import {ServerAPI} from "../1. Core/ServerAPI";
import Label from "../1. Core/Controls/Label/Label";
import TextField from "../1. Core/Controls/TextField/TextField";

interface PaymentBotHealth {
    configured: boolean;
    instanceId: string;
    isLeader: boolean;
    leaderInstanceId?: string;
    leaderHeartbeatAt?: string;
    leaderStale?: boolean;
    pollingActive: boolean;
    botUsername?: string;
    lastWebhookUrl?: string;
    lastHealthCheckAt?: string;
    lastUpdateAt?: string;
    lastHealthError?: string;
    cacheStatus?: string;
    configSource?: string;
    tokenMasked?: string;
    configWritable?: boolean;
    botRunsHere?: boolean;
}

interface PaymentBotGameInstance {
    instanceId: string;
    isLeader: boolean;
    pollingActive: boolean;
    botUsername?: string;
    lastHealthCheckAt?: string;
    lastHealthError?: string;
}

interface PaymentBotLogEntry {
    eventId: number;
    timestamp: string;
    level: string;
    message: string;
    loggerName: string;
    traceLine?: string;
    instanceId?: string;
}

interface PaymentBotOrder {
    id: string;
    sku: string;
    status: string;
    userId: number;
    chatId?: string;
    createdAt?: string;
}

export default class PaymentBotPanel extends Control {
    private _topAppBar: TopAppBar;
    private _contentNode: HTMLElement;
    private _statusNode: HTMLElement;
    private _gameInstancesTable: DataTable;
    private _logsTable: DataTable;
    private _ordersTable: DataTable;
    private _levelSelect: Select;
    private _instanceSelect: Select;
    private _tokenInput: TextField;

    constructor(settings?: object) {
        super(settings);
        this._render();
    }

    _render() {
        this.np_render();

        this._topAppBar = new TopAppBar({
            Caption: "Telegram Pay Bot",
            ParentNode: this._root,
            Class: "top-app-bar--no-shadow top-app-bar--light library-box-list__app-bar",
            RightItems: [
                {
                    Content: "Refresh",
                    EvClick: () => this.refresh(),
                },
            ],
        });

        this._contentNode = NP.createNode(this._root);
        this._contentNode.style.padding = "16px 24px";
        this._contentNode.style.boxSizing = "border-box";
        this._contentNode.style.overflow = "auto";

        this._statusNode = NP.createNode(this._contentNode);
        this._statusNode.style.marginBottom = "16px";
        this._statusNode.style.lineHeight = "1.6";

        new Label({
            ParentNode: this._contentNode,
            Content: "Payment bot instances — live status from Redis",
        });

        this._gameInstancesTable = new DataTable({
            ParentNode: this._contentNode,
            ShowPaginator: false,
            Width: 900,
            Columns: [
                {Content: "Instance", PropertyContent: "instanceId", Width: "120px"},
                {Content: "Leader", PropertyContent: "isLeader", Width: "60px"},
                {Content: "Polling", PropertyContent: "pollingActive", Width: "70px"},
                {Content: "Bot", PropertyContent: "botUsername", Width: "140px"},
                {Content: "Health check (UTC)", PropertyContent: "lastHealthCheckAt", Width: "150px"},
                {Content: "Error", PropertyContent: "lastHealthError", Width: "200px"},
            ],
        });
        this._gameInstancesTable.getRoot().style.marginBottom = "16px";

        new Label({
            ParentNode: this._contentNode,
            Content: `This page host (${window.location.hostname})`,
        });

        const actions = NP.createNode(this._contentNode);
        actions.style.marginBottom = "16px";
        actions.style.display = "flex";
        actions.style.gap = "12px";
        actions.style.alignItems = "center";

        new Button({
            ParentNode: actions,
            Content: "Take leadership (this server)",
            EvClick: async () => {
                try {
                    await ServerAPI.post<PaymentBotHealth>("/admin/payment-bot/take-leadership", {});
                    NP.showMessage({Content: "Leadership transfer requested on this server"});
                    await this.refresh();
                } catch (e) {
                    NP.showMessage({Content: "Only available on instances with the payment bot enabled"});
                }
            },
        });

        new Button({
            ParentNode: actions,
            Content: "Restart polling",
            EvClick: async () => {
                try {
                    await ServerAPI.post<PaymentBotHealth>("/admin/payment-bot/restart-polling", {});
                    NP.showMessage({Content: "Polling restart requested"});
                    await this.refresh();
                } catch (e) {
                    NP.showMessage({Content: "Only available on instances with the payment bot enabled"});
                }
            },
        });

        this._levelSelect = new Select({
            ParentNode: actions,
            FloatingLabel: "Log level",
            Items: [
                {Content: "All", Value: ""},
                {Content: "WARN+", Value: "WARN"},
                {Content: "ERROR", Value: "ERROR"},
            ],
            Value: "",
            EvItemSelected: () => this.refreshLogs(),
        });
        this._levelSelect.getRoot().style.width = "160px";

        this._instanceSelect = new Select({
            ParentNode: actions,
            FloatingLabel: "Server instance",
            Items: [{Content: "All servers", Value: ""}],
            Value: "",
            EvItemSelected: () => this.refreshLogs(),
        });
        this._instanceSelect.getRoot().style.width = "280px";

        const tokenRow = NP.createNode(this._contentNode);
        tokenRow.style.marginBottom = "16px";
        tokenRow.style.display = "flex";
        tokenRow.style.gap = "12px";
        tokenRow.style.alignItems = "flex-end";
        tokenRow.style.flexWrap = "wrap";

        this._tokenInput = new TextField({
            ParentNode: tokenRow,
            Width: 420,
            FloatingLabel: "Telegram bot token (application.telegram-bot-token)",
        });

        new Button({
            ParentNode: tokenRow,
            Content: "Save token",
            EvClick: async () => {
                const token = (this._tokenInput.getValue() || "").trim();
                if (!token) {
                    NP.showMessage({Content: "Enter bot token"});
                    return;
                }
                try {
                    await ServerAPI.post<PaymentBotHealth>("/admin/payment-bot/set-token", {token});
                    this._tokenInput.setValue("");
                    NP.showMessage({Content: "Token saved to classpath application.properties"});
                    await this.refresh();
                } catch (e) {
                    NP.showMessage({Content: "Failed to save token (only on instances with the payment bot enabled)"});
                }
            },
        });

        new Label({
            ParentNode: this._contentNode,
            Content: "Recent logs (all app servers → shared database)",
        });

        this._logsTable = new DataTable({
            ParentNode: this._contentNode,
            ShowPaginator: false,
            Width: 900,
            Columns: [
                {Content: "Time (UTC)", PropertyContent: "timestamp", Width: "140px"},
                {Content: "Instance", PropertyContent: "instanceId", Width: "120px"},
                {Content: "Level", PropertyContent: "level", Width: "60px"},
                {Content: "Message", PropertyContent: "message", Width: "420px"},
                {Content: "Trace", PropertyContent: "traceLine", Width: "160px"},
            ],
        });
        this._logsTable.getRoot().style.marginBottom = "20px";

        new Label({
            ParentNode: this._contentNode,
            Content: "Recent orders",
        });

        this._ordersTable = new DataTable({
            ParentNode: this._contentNode,
            ShowPaginator: false,
            Width: 900,
            Columns: [
                {Content: "Created (UTC)", PropertyContent: "createdAt", Width: "150px"},
                {Content: "Status", PropertyContent: "status", Width: "80px"},
                {Content: "SKU", PropertyContent: "sku", Width: "120px"},
                {Content: "User", PropertyContent: "userId", Width: "70px"},
                {Content: "Chat", PropertyContent: "chatId", Width: "120px"},
                {Content: "Order ID", PropertyContent: "id", Width: "260px"},
            ],
        });
    }

    getTopAppBar() {
        return this._topAppBar;
    }

    async refresh() {
        await Promise.all([
            this.refreshGameInstances(),
            this.refreshStatus(),
            this.refreshLogInstances(),
            this.refreshLogs(),
            this.refreshOrders(),
        ]);
    }

    private async refreshGameInstances() {
        const instances = await ServerAPI.get<PaymentBotGameInstance[]>("/admin/payment-bot/game-instances");
        this._gameInstancesTable.setRows(
            (instances || []).map((row) => ({
                instanceId: row.instanceId.length > 8 ? row.instanceId.substring(0, 8) + "…" : row.instanceId,
                isLeader: row.isLeader ? "yes" : "no",
                pollingActive: row.pollingActive ? "active" : "stopped",
                botUsername: row.botUsername ? `@${row.botUsername}` : "—",
                lastHealthCheckAt: row.lastHealthCheckAt || "—",
                lastHealthError: row.lastHealthError || "—",
            })),
        );
    }

    private async refreshStatus() {
        const status = await ServerAPI.get<PaymentBotHealth>("/admin/payment-bot/status");
        if (!status) {
            this._statusNode.textContent = "Status unavailable";
            return;
        }
        const lines = [
            `Bot runs on this host: ${status.botRunsHere ? "yes" : "no"}`,
            `Configured: ${status.configured ? "yes" : "no"}`,
            `Bot: @${status.botUsername || "—"}`,
            `Token (runtime): ${status.tokenMasked || "—"}`,
            `Config file: ${status.configSource || "classpath:application.properties"}`,
            `Config writable: ${status.configWritable ? "yes" : "no"}`,
            `This instance: ${status.instanceId}`,
            `Leader: ${status.isLeader ? "yes (this server)" : "no"} (${status.leaderInstanceId || "—"})`,
            `Leader heartbeat (UTC): ${status.leaderHeartbeatAt || "—"}`,
            `Leader stale: ${status.leaderStale ? "yes" : "no"}`,
            `Polling: ${status.pollingActive ? "active" : "stopped"}`,
            `Cache: ${status.cacheStatus || "—"}`,
            `Last health check (UTC): ${status.lastHealthCheckAt || "—"}`,
            `Last message (UTC): ${status.lastUpdateAt || "—"}`,
            `Webhook: ${status.lastWebhookUrl || "none"}`,
        ];
        if (!status.botRunsHere) {
            lines.unshift(
                "⚠ Платёжный бот отключён на этом экземпляре. Состояние инстансов с общим Redis показано в таблице выше.",
            );
            lines.unshift("Смотрите таблицу «Payment bot instances» выше — там статус всех инстансов.");
        }
        if (!status.isLeader && status.botRunsHere) {
            lines.push("Hint: use «Take leadership» if the leader instance is dead or stuck.");
        }
        if ((status.cacheStatus || "").includes("Fail")) {
            lines.push("⚠ Redis unavailable — leader election between game servers may be broken.");
        }
        lines.push("Logs below: all servers → shared database.");
        if (status.lastHealthError) {
            lines.push(`Last error: ${status.lastHealthError}`);
        }
        if (!status.botRunsHere) {
            lines.push("Управление ботом доступно на инстансах с включённым application.payment-bot-enabled.");
        }
        this._statusNode.innerHTML = "";
        for (const line of lines) {
            const div = document.createElement("div");
            div.textContent = line;
            this._statusNode.appendChild(div);
        }
    }

    private async refreshLogInstances() {
        const instances = await ServerAPI.get<string[]>("/admin/payment-bot/log-instances");
        const current = this._instanceSelect?.getValue?.() || "";
        const items = [{Content: "All servers", Value: ""}];
        for (const id of instances || []) {
            const short = id.length > 8 ? id.substring(0, 8) + "…" : id;
            items.push({Content: short, Value: id});
        }
        this._instanceSelect.setItems(items);
        if (current && items.some((i) => i.Value === current)) {
            this._instanceSelect.setValue(current);
        }
    }

    private async refreshLogs() {
        const level = this._levelSelect?.getValue?.() || "";
        const instanceId = this._instanceSelect?.getValue?.() || "";
        const params = new URLSearchParams({limit: "200"});
        if (level) {
            params.set("level", level);
        }
        if (instanceId) {
            params.set("instanceId", instanceId);
        }
        const logs = await ServerAPI.get<PaymentBotLogEntry[]>(`/admin/payment-bot/logs?${params.toString()}`);
        this._logsTable.setRows(
            (logs || []).map((row) => ({
                ...row,
                instanceId: row.instanceId
                    ? (row.instanceId.length > 8 ? row.instanceId.substring(0, 8) + "…" : row.instanceId)
                    : "—",
            })),
        );
    }

    private async refreshOrders() {
        const orders = await ServerAPI.get<PaymentBotOrder[]>("/admin/payment-bot/orders?limit=50");
        this._ordersTable.setRows(orders || []);
    }

    setWidth(value: number) {
        super.setWidth(value);
        if (this._contentNode) {
            this._contentNode.style.width = `${value}px`;
        }
        if (this._logsTable) {
            this._logsTable.setWidth(Math.max(320, value - 48));
        }
        if (this._ordersTable) {
            this._ordersTable.setWidth(Math.max(320, value - 48));
        }
    }

    setHeight(value: number) {
        super.setHeight(value);
        if (this._contentNode) {
            this._contentNode.style.height = `${value - 64}px`;
        }
        if (this._logsTable) {
            this._logsTable.setHeight(Math.max(180, (value - 64) * 0.45));
        }
        if (this._ordersTable) {
            this._ordersTable.setHeight(Math.max(140, (value - 64) * 0.25));
        }
    }
}
